import React from 'react';
import styled from 'styled-components';
import { GatsbyImage, IGatsbyImageData, getImage } from 'gatsby-plugin-image';
import { P } from '@components/shared/Typography';
import { PageSlice } from '@contracts';
import TwoColumn from '@components/shared/TwoColumn';
import useDesktop from './useDesktop';

const MIN_DESKTOP_SIZE = 1024;

interface IEndorsementItemProp {
  endorsement: {
    name: {
      text: string;
    };
    profile_image: {
      gatsbyImageData: IGatsbyImageData;
      alt: string;
    };
    quote: {
      text: string;
    };
    title: {
      text: string;
    };
  };
  textColor?: string;
}

const EndorsementItem: React.FC<IEndorsementItemProp> = ({ endorsement, textColor }) => {
  const isMobile = !useDesktop(MIN_DESKTOP_SIZE);
  const image = getImage(endorsement.profile_image);
  const imageAlt = endorsement.profile_image.alt ?? '';
  return (
    <ChildContainer $isMobile={isMobile}>
      <IconWrap>{image && <GatsbyImage image={image} alt={imageAlt} objectFit="contain" />}</IconWrap>
      <TextWrap $textColor={textColor}>{endorsement.quote.text}</TextWrap>
      <DescriptionWrap $textColor={textColor}>
        <b>{endorsement.name.text}</b>
        <br />
        {endorsement.title.text}
      </DescriptionWrap>
    </ChildContainer>
  );
};

interface Props {
  slice: PageSlice;
}

const Endorsements: React.FC<Props> = ({ slice }) => {
  const title = slice.primary?.title.text;
  const backgroundImage = slice.primary?.background_image.url;
  const backgroundColor = slice.primary?.background_color;
  const textColor = slice.primary?.text_color;
  const isMobile = !useDesktop(1024);
  const endorsementItems = slice.items ?? [];

  return (
    <Container $backgroundImage={backgroundImage} $isMobile={isMobile} $backgroundColor={backgroundColor}>
      {title && <TitleWrap $textColor={textColor}>{title}</TitleWrap>}
      <div style={{ display: 'flex', alignItems: 'start' }}>
        <TwoColumn alignment="top">
          {endorsementItems.map((item, index) => (
            <EndorsementItem key={index} endorsement={item} textColor={textColor} />
          ))}
        </TwoColumn>
      </div>
    </Container>
  );
};

export default Endorsements;

interface IContainer {
  $backgroundImage?: string;
  $backgroundColor?: string;
  $isMobile?: boolean;
  $textColor?: string;
}

const ChildContainer = styled.div<IContainer>`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  padding: ${(props) => (props.$isMobile ? '40px 30px' : '120px 80px')};
`;

const Container = styled.div<IContainer>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${(props) => `url(${props.$backgroundImage})`};
  background-repeat: ${(props) => (props.$isMobile ? 'no-repeat' : 'auto')};
  background-size: ${(props) => (props.$isMobile ? '100% 287px, 100% 100%' : 'contain')};
  background-color: ${(props) => props.$backgroundColor};

  @media (max-width: 768px) {
    background: #f1f6e4;
  }
`;

const TitleWrap = styled.div<IContainer>`
  color: ${(props) => props.$textColor};
  font-family: Domaine;
  font-size: 30px;
  font-style: normal;
  font-weight: 500;
  line-height: 39px;
  letter-spacing: -0.01em;
  text-align: center;

  padding: 90px 30px 0 30px;

  @media (max-width: 1024px) {
    padding-top: 60px;
    font-family: Domaine;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    text-align: center;
  }
`;

const IconWrap = styled.div`
  img {
    display: block;
    max-width: 129px;
    max-height: 129px;
    border-radius: 100%;
  }
`;

const TextWrap = styled(P)<IContainer>`
  color: ${(props) => props.$textColor};
  text-align: center;
  max-width: 374px;
  padding: 30px 0 20px 0;
  font-family: BrownStd;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 27px;

  text-align: center;

  @media (max-width: 1024px) {
    font-family: BrownStd;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;
  }
`;

const DescriptionWrap = styled(P)<IContainer>`
  color: ${(props) => props.$textColor};
  text-align: center;
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  letter-spacing: 0;
  text-align: center;

  @media (max-width: 1024px) {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0;
    text-align: center;
  }
`;
